import { useRef, useEffect } from "react";

/**
 * Fades + lifts its children into view once they enter the viewport.
 * Uses IntersectionObserver and toggles the `.is-visible` class (see index.css).
 * `delay` (ms) staggers the animation; pass `as` to change the wrapper element.
 */
export default function Reveal({
  children,
  className = "",
  delay = 0,
  style,
  as: Tag = "div",
}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reveal = () => el.classList.add("is-visible");

    // No IntersectionObserver (or SSR) → just show it.
    if (typeof IntersectionObserver === "undefined") {
      reveal();
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          reveal();
          io.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);

    // If the element is already on-screen when it mounts — e.g. content that
    // arrives after an async fetch (toppers, etc.) while its section is in view —
    // reveal it right away so it can never get stuck hidden.
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < vh && rect.bottom > 0) {
      reveal();
      io.disconnect();
    }

    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${className}`}
      style={{ ...style, ...(delay ? { transitionDelay: `${delay}ms` } : {}) }}
    >
      {children}
    </Tag>
  );
}
